import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { ApiError, AiPrompts, SystemSettingsService } from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useDialogState } from "hooks/useDialogState";
import { AiPromptsTable } from "./components/AiPromptsTable";
import { EditAiPromptDialog } from "./components/EditAiPromptDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { SnackbarAlert } from "components/SnackbarAlert/SnackbarAlert";

interface PromptItem {
  key: keyof AiPrompts;
  name: string;
  description: string;
  value: string;
}

const AiPromptsTab: React.FC = () => {
  const [editDialogOpen, openEditDialog, closeEditDialog] = useDialogState();
  const [selectedPrompt, setSelectedPrompt] = React.useState<PromptItem | null>(null);

  const {
    data: aiPrompts,
    isLoading,
    error,
    refetch
  } = useQuery<AiPrompts, ApiError>(["getAiPrompts"], {
    queryFn: SystemSettingsService.getAiPrompts
  });

  const {
    mutateAsync: updatePrompts,
    isLoading: isUpdating,
    error: updateError,
    isSuccess: updateSuccess
  } = useMutation<void, ApiError, { requestBody?: AiPrompts }>(SystemSettingsService.updateAiPrompts);

  const promptItems: PromptItem[] = React.useMemo(() => {
    if (!aiPrompts) {
      return [];
    }

    return [
      {
        key: "linguisticQualityReviewPrompt",
        name: "Text Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of text",
        value: aiPrompts.linguisticQualityReviewPrompt
      },
      {
        key: "pdfLinguisticQualityReviewPrompt",
        name: "PDF Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of PDF documents (PDF comparison)",
        value: aiPrompts.pdfLinguisticQualityReviewPrompt
      },
      {
        key: "xliffLinguisticQualityReviewPrompt",
        name: "XLIFF Linguistic Quality Review",
        description: "Prompt for AI linguistic quality review of XLIFF documents (source vs target comparison)",
        value: aiPrompts.xliffLinguisticQualityReviewPrompt
      },
      {
        key: "linguisticQualityReviewsSummaryPrompt",
        name: "Linguistic Quality Review Summary",
        description: "Prompt for AI to summarize multiple linguistic quality reviews",
        value: aiPrompts.linguisticQualityReviewsSummaryPrompt
      },
      {
        key: "postEditingPrompt",
        name: "Text Post Editing",
        description: "Prompt for AI post editing of machine translated text",
        value: aiPrompts.postEditingPrompt
      },
      {
        key: "filePostEditingPrompt",
        name: "File Post Editing",
        description: "Prompt for AI file post editing",
        value: aiPrompts.filePostEditingPrompt
      }
    ];
  }, [aiPrompts]);

  const handleEditClick = React.useCallback(
    (prompt: PromptItem) => {
      setSelectedPrompt(prompt);
      openEditDialog();
    },
    [openEditDialog]
  );

  const handleSavePrompt = React.useCallback(
    async (updatedValue: string) => {
      if (!selectedPrompt || !aiPrompts) {
        return;
      }

      const updatedPrompts: AiPrompts = {
        ...aiPrompts,
        [selectedPrompt.key]: updatedValue
      };

      await updatePrompts({
        requestBody: updatedPrompts
      });

      refetch();
      closeEditDialog();
    },
    [selectedPrompt, aiPrompts, updatePrompts, refetch, closeEditDialog]
  );

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Card sx={{ mb: 1 }} elevation={0}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            AI Prompts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage AI prompts used throughout the system for various automated processes. These prompts define how AI
            models analyze and process content.
          </Typography>
        </CardContent>
      </Card>

      <AiPromptsTable
        prompts={promptItems}
        onEditClick={handleEditClick}
        isUpdating={isUpdating}
        updateError={updateError}
      />

      {editDialogOpen && selectedPrompt && (
        <EditAiPromptDialog
          prompt={selectedPrompt}
          onClose={closeEditDialog}
          onSave={handleSavePrompt}
          isLoading={isUpdating}
        />
      )}

      {updateError && <SnackbarApiError error={updateError} />}
      {updateSuccess && <SnackbarAlert message="AI prompt updated successfully" severity="success" />}
    </Box>
  );
};

export { AiPromptsTab };
